import React, { useMemo, useState } from 'react';
import {
  Title,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Text,
  Anchor,
  Center,
  Group,
  Stack,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../../hooks/useAuthenticatedData';
import config from '../../../../../config';
import MembershipPopup from '../../MembershipPopup';
import SQLEditorPopup from '../../SQLEditorPopup';

const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Title order={6} size="sm" mb={4}>
      Legend
    </Title>
    <Stack gap={4}>
      {[
        { color: 'var(--mantine-color-green-6)', label: 'Margin ≥ 20%' },
        { color: 'var(--mantine-color-yellow-6)', label: 'Margin between 15% and 20%' },
        { color: 'var(--mantine-color-red-6)', label: 'Margin < 15%' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function RepairsDepartmentInventoryPlannedSpendDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';

  const url = `${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/inventory-planned-spend-details?period=${period}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  const handleTabChange = (newPeriod) => {
    navigate({
      pathname: location.pathname,
      search: `?period=${newPeriod}`,
    });
  };

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      // Extract values from the nested structure
      entered: item.values?.entered,
      partnumber: item.values?.partnumber,
      condition: item.values?.condition,
      accountName: item.values?.accountname,
      fullname: item.values?.fullname,
      qtyOrdered: item.values?.qtyordered,
      unitPrice: item.values?.unitprice,
      unitSellPrice: item.values?.unitsellprice,
      spend: item.values?.spend,
      projectedRev: item.values?.projectedrev,
      projectedMargin: item.values?.projectedmargin,
      marginPercent: item.values?.marginpercent,
      shipDate: item.values?.shipdate,
      dueDate: item.values?.duedate,
      isBer: item.values?.isber,
      onRepairTeam: item.values?.onrepairteam,
      docNo: item.values?.docno,
      userLine: item.values?.userline,
      docType: item.values?.doctype,
    }));
  }, [data]);

  const generateExportFileName = () => `Repairs-${period}-inventory-planned-spend-details`;

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Entered': row.entered ? new Date(row.entered).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Condition': row.condition || '',
      'Vendor': row.accountName || '',
      'Repair Coordinator': row.fullname || '',
      'Qty': row.qtyOrdered || '',
      'Unit Price': row.unitPrice || '',
      'Unit Sell Price': row.unitSellPrice || '',
      'Spend': row.spend || '',
      'Projected Revenue': row.projectedRev || '',
      'Projected Margin': row.projectedMargin || '',
      'Margin %': row.marginPercent ? `${Number(row.marginPercent).toFixed(2)}%` : '',
      'Ship Date': row.shipDate ? new Date(row.shipDate).toLocaleDateString() : '',
      'Due Date': row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '',
      'BER?': row.isBer || '',
      'Team?': row.onRepairTeam || '',
      'Pentagon Doc#': row.docNo || '',
      'Line': row.userLine || '',
      'Doc Type': row.docType || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'entered',
        header: 'Entered',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'condition', header: 'Condition', size: 100 },
      { accessorKey: 'accountName', header: 'Vendor', size: 150 },
      { accessorKey: 'fullname', header: 'Repair Coordinator', size: 150 },
      { accessorKey: 'qtyOrdered', header: 'Qty', size: 80 },
      {
        accessorKey: 'unitPrice',
        header: 'Unit Price',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'unitSellPrice',
        header: 'Unit Sell Price',
        size: 140,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'spend',
        header: 'Spend',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedRev',
        header: 'Projected Revenue',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedMargin',
        header: 'Projected Margin',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `$${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'marginPercent',
        header: 'Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'shipDate',
        header: 'Ship Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'isBer', header: 'BER?', size: 60 },
      { accessorKey: 'onRepairTeam', header: 'Team?', size: 60 },
      { accessorKey: 'docNo', header: 'Pentagon Doc#', size: 120 },
      { accessorKey: 'userLine', header: 'Line', size: 80 },
      { accessorKey: 'docType', header: 'Doc Type', size: 140 },
    ],
    []
  );

  const getRowStyle = (row) => {
    const margin = Number(row.original.marginPercent);
    if (isNaN(margin)) return {};

    if (margin >= 20) {
      return {
        backgroundColor: 'var(--mantine-color-green-1)',
        color: 'var(--mantine-color-green-9)',
      };
    }
    if (margin >= 15 && margin < 20) {
      return {
        backgroundColor: 'var(--mantine-color-yellow-1)',
        color: 'var(--mantine-color-yellow-9)',
      };
    }
    if (margin < 15) {
      return {
        backgroundColor: 'var(--mantine-color-red-1)',
        color: 'var(--mantine-color-red-9)',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      <Text
        component="h1"
        style={{
          fontFamily: 'Orbitron, sans-serif',
          color: '#FDB813',
          fontStyle: 'italic',
          fontSize: '1.5rem',
          marginBottom: 0,
          textAlign: 'left'
        }}
      >
        Repairs Scorecard - Planned Repair Spend Details
      </Text>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_dept_planned_inventory_spend_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=repairs_dept_planned_inventory_spend_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Title order={6} size="sm" mb={4}>
            About the Data
          </Title>
          <Text size="xs" c="dimmed" mb="xs">
            Summary Data: The repair spend is taken from the repair order and the margin is calculated based on that repair price vs. the sell price for the part in the 
            expected repaired condition at the time the repair order was created. Parts without a sell price are not included in the margin calculation.
          </Text>
          <Text size="xs" c="dimmed">
            Detail Data: These are all repair orders. When a repair order is received, it isn't removed from this list and it is added to the "landed spend" list. 
            All repair orders are listed here - the ones missing data or that are BER are clear to see.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      <Box mt="sm">
        <Tabs value={period} onChange={handleTabChange} mb="sm">
          <Tabs.List>
            <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

      {/* Pop-up dialogs */}
      <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        opened={openSummaryQuery}
        onClose={() => setOpenSummaryQuery(false)}
        title="Summary Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/queries/inventory-planned-spend-summary-query`}
      />
      <SQLEditorPopup
        opened={openDetailsQuery}
        onClose={() => setOpenDetailsQuery(false)}
        title="Details Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/repairs/department/scorecard/queries/inventory-planned-spend-details-query`}
      />
    </Paper>
  );
}

export default RepairsDepartmentInventoryPlannedSpendDetails;